import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTypists } from '../lib/typing-store';

/**
 * "Maya is typing…", and with sneak peek on (`behaviour.showTypingToStaff`) the shopper's draft
 * on its own line as they type. The draft lives only in the in-memory typing store until the
 * indicator fades or the message lands; it is never persisted or logged.
 */
export function TypingLine({
  conversationId,
  shopper,
}: {
  conversationId: string;
  shopper: string;
}) {
  const { t } = useTranslation('conversations');
  const typists = useTypists(conversationId);
  const visitor = typists.find((typist) => typist.authorType === 'visitor');
  const staff = typists.filter((typist) => typist.authorType === 'staff');
  if (!visitor && staff.length === 0) return <div className="h-5" aria-hidden />;
  const draft = visitor?.draft?.trim() ? visitor.draft : null;
  return (
    <div className="text-muted-foreground min-h-5 space-y-0.5 px-4 pb-1 text-xs" aria-live="polite">
      {visitor ? <p className="truncate">{t('typing.visitor', { name: shopper })}</p> : null}
      {draft ? (
        <p
          className="bg-muted text-foreground flex items-start gap-1.5 rounded-md px-2 py-1"
          data-testid="sneak-peek"
        >
          <Eye className="text-muted-foreground mt-0.5 size-3 shrink-0" aria-hidden />
          <span className="sr-only">{t('typing.sneakPeek')}</span>
          <span className="line-clamp-2 break-words italic">{draft}</span>
        </p>
      ) : null}
      {staff.map((typist) => (
        <p key={typist.key} className="truncate">
          {t(typist.internal ? 'typing.staffNote' : 'typing.staff', {
            name: typist.name ?? t('events.someone'),
          })}
        </p>
      ))}
    </div>
  );
}
